import { toast } from 'react-toastify';
import React, { useState } from 'react';
import { IDataCatalog, updateDataCatalog, VerdocsEndpoint } from '@verdocs/js-sdk';
import { Badge, Button, Spinner, Switch } from '../Components';
import { Dialog } from './Dialog';

interface ShareDataCatalogDialogProps {
  catalog: IDataCatalog;
  onClose: () => void;
  onSaved: () => void;
  /** The version a submission would send. */
  submitVersion: number;
  /** Opens the public marketplace submission. Left out when there's nothing to submit or the person can't. */
  onSubmit?: () => void;
}

export function ShareDataCatalogDialog({ catalog, onClose, onSaved, submitVersion, onSubmit }: ShareDataCatalogDialogProps) {
  const [ shared, setShared ] = useState(catalog.sharing === 'shared');
  const [ submitting, setSubmitting ] = useState(false);
  const inReview = catalog.submission?.status === 'submitted';

  const handleSave = () => {
    if (submitting) {
      return;
    }

    setSubmitting(true);
    updateDataCatalog(VerdocsEndpoint.getDefault(), catalog.organization_id, catalog.id, { sharing: shared ? 'shared' : 'private' })
      .then(() => {
        setSubmitting(false);
        onSaved();
      })
      .catch((e: any) => {
        setSubmitting(false);
        toast.error(`Error saving sharing: ${e.response?.data?.message || e.message}`);
      });
  };

  return (
    <Dialog onCancel={onClose}>
      <div className="text-lg font-medium text-[#33364b]">Share settings</div>
      <div className="mb-4 mt-0.5 text-[13px] text-gray-500">{catalog.name}</div>

      <div className="rounded-lg border border-[#ebecf0] px-3">
        <label className="flex cursor-pointer items-center justify-between gap-4 border-b border-[#f2f3f5] py-2.5">
          <span>
            <span className="block text-[13px] font-medium text-[#33364b]">Share with sub-accounts</span>
            <span className="mt-0.5 block text-[12px] text-gray-500">
              Every organization below this one can find it in the marketplace and import a copy of each released version.
            </span>
          </span>
          <Switch
            checked={shared}
            onCheckedChange={setShared}
            className="data-[state=checked]:bg-[#654dcb] data-[state=unchecked]:bg-gray-200"
          />
        </label>
        <div className="flex items-center justify-between gap-4 py-2.5">
          <span>
            <span className="flex items-center gap-1.5 text-[13px] font-medium text-[#33364b]">
              List publicly
              {!!catalog.public_version && <Badge tone="muted">Public, version {catalog.public_version}</Badge>}
              {inReview && <Badge tone="muted">Version {catalog.submission?.version} in review</Badge>}
            </span>
            <span className="mt-0.5 block text-[12px] text-gray-500">
              Every organization can find it in the marketplace and import a copy, once Verdocs approves a version.
            </span>
          </span>
          {onSubmit && (
            <Button
              size="xsmall"
              variant="secondary"
              label={`Submit version ${submitVersion}`}
              onClick={onSubmit}
              disabled={submitting}
            />
          )}
        </div>
      </div>

      {shared && catalog.latest_version === 0 && (
        <div className="mt-3 rounded-md border border-[#f0d9b5] bg-[#fdf8ef] px-3 py-2 text-[12px] leading-normal text-[#95591a]">
          Sub-accounts see it once you release a version.
        </div>
      )}

      <div className="mt-4 flex flex-row items-center justify-end gap-2 border-t border-[#f2f3f5] pt-4">
        {submitting && <Spinner mode="dark" />}
        <Button
          type="button"
          size="small"
          variant="secondary"
          label="Cancel"
          onClick={onClose}
          disabled={submitting}
        />
        <Button
          size="small"
          label="Save"
          onClick={handleSave}
          disabled={submitting}
        />
      </div>
    </Dialog>
  );
}
